import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import { useAuth } from 'react-oidc-context';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { useParams, useNavigate } from 'react-router-dom';
import cloudApi from '../../app/api/cloudApi';
import procoreApi from '../../app/api/procoreApi';
import CardHeader from '@mui/material/CardHeader';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { Icon } from '@iconify/react';

export default function ProcoreSettingsPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { account } = useSelector((state) => state.app);

  const [pageLoading, setPageLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasConnection, setHasConnection] = useState(false);
  const [dailyPushHourUtc, setDailyPushHourUtc] = useState(6);
  const [lastAutoPushDateUtc, setLastAutoPushDateUtc] = useState(null);
  const [savingConnection, setSavingConnection] = useState(false);

  const [mapping, setMapping] = useState({
    procoreCompanyId: '',
    procoreProjectId: '',
    displayName: '',
    isEnabled: true,
    autoPushEnabled: false,
    hasExisting: false,
  });

  useEffect(() => {
    if (account?.id && siteId) {
      loadData();
    }
  }, [account, siteId]);

  const loadData = async () => {
    setPageLoading(true);
    try {
      // Load site info
      const siteResponse = await cloudApi.getSite(auth.user.access_token, siteId);
      setSite(siteResponse.data);

      // Check if account has a Procore connection
      const connectionResponse = await procoreApi.getConnection(auth.user.access_token, account.id);
      setHasConnection(connectionResponse.data.hasConnection);
      if (connectionResponse.data.hasConnection) {
        setDailyPushHourUtc(connectionResponse.data.dailyPushHourUtc ?? 6);
        setLastAutoPushDateUtc(connectionResponse.data.lastAutoPushDateUtc);
      }

      // Load existing mapping for this site
      const mappingResponse = await procoreApi.getSiteMapping(auth.user.access_token, account.id, siteId);
      if (mappingResponse.data.hasMapping) {
        setMapping({
          procoreCompanyId: String(mappingResponse.data.procoreCompanyId),
          procoreProjectId: String(mappingResponse.data.procoreProjectId),
          displayName: mappingResponse.data.displayName || '',
          isEnabled: mappingResponse.data.isEnabled,
          autoPushEnabled: mappingResponse.data.autoPushEnabled ?? false,
          hasExisting: true,
        });
      }
    } catch (error) {
      console.error('Failed to load Procore settings:', error);
      enqueueSnackbar('Failed to load Procore settings', { variant: 'error' });
    }
    setPageLoading(false);
  };

  const handleSave = async () => {
    if (!mapping.procoreCompanyId || isNaN(parseInt(mapping.procoreCompanyId, 10))) {
      enqueueSnackbar('Valid Procore Company ID is required', { variant: 'error' });
      return;
    }
    if (!mapping.procoreProjectId || isNaN(parseInt(mapping.procoreProjectId, 10))) {
      enqueueSnackbar('Valid Procore Project ID is required', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      await procoreApi.saveSiteMapping(auth.user.access_token, {
        accountId: account.id,
        siteId,
        procoreCompanyId: parseInt(mapping.procoreCompanyId, 10),
        procoreProjectId: parseInt(mapping.procoreProjectId, 10),
        displayName: mapping.displayName || null,
        isEnabled: mapping.isEnabled,
        autoPushEnabled: mapping.autoPushEnabled,
      });
      enqueueSnackbar('Procore site mapping saved successfully', { variant: 'success' });
      setMapping((prev) => ({ ...prev, hasExisting: true }));
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.error || error.response?.data || 'Failed to save site mapping';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await procoreApi.deleteSiteMapping(auth.user.access_token, account.id, siteId);
      enqueueSnackbar('Procore site mapping removed', { variant: 'success' });
      setMapping({
        procoreCompanyId: '',
        procoreProjectId: '',
        displayName: '',
        isEnabled: true,
        autoPushEnabled: false,
        hasExisting: false,
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to remove site mapping', { variant: 'error' });
    }
    setSaving(false);
  };

  const handleSaveDailyPushHour = async () => {
    setSavingConnection(true);
    try {
      await procoreApi.updateDailyPushSchedule(auth.user.access_token, account.id, dailyPushHourUtc);
      enqueueSnackbar('Daily push schedule saved', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to save daily push schedule', { variant: 'error' });
    }
    setSavingConnection(false);
  };

  if (pageLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Procore Settings - ${site?.name || 'Site'} | CloudLoq`}</title>
      </Helmet>

      <Container maxWidth="lg">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/sites" underline="hover" color="inherit">
            Sites
          </Link>
          <Typography color="text.primary">{site?.name || 'Site'}</Typography>
          <Typography color="text.primary">Procore Settings</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ mb: 3 }}>
          Procore Settings - {site?.name}
        </Typography>

        <Stack spacing={3}>
          {/* Connection Status */}
          {!hasConnection && (
            <Alert severity="warning">
              No Procore connection configured for this account. Please configure OAuth credentials in{' '}
              <Link component={RouterLink} to="/account-settings" underline="hover">
                Account Settings
              </Link>{' '}
              first.
            </Alert>
          )}

          {/* Site Mapping Card */}
          <Card>
            <CardHeader
              title="Procore Project Mapping"
              subheader="Map this CloudLoq site to a specific Procore company and project"
              action={
                mapping.hasExisting ? (
                  <Chip
                    label={mapping.isEnabled ? 'Connected' : 'Disabled'}
                    color={mapping.isEnabled ? 'success' : 'default'}
                    size="small"
                  />
                ) : (
                  <Chip label="Not Configured" color="warning" size="small" />
                )
              }
            />
            <Box sx={{ p: 3, pt: 0 }}>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Procore Company ID"
                    value={mapping.procoreCompanyId}
                    onChange={(e) => setMapping((prev) => ({ ...prev, procoreCompanyId: e.target.value }))}
                    type="number"
                    helperText="The Procore company ID this site maps to"
                    disabled={!hasConnection}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Procore Project ID"
                    value={mapping.procoreProjectId}
                    onChange={(e) => setMapping((prev) => ({ ...prev, procoreProjectId: e.target.value }))}
                    type="number"
                    helperText="The Procore project ID this site maps to"
                    disabled={!hasConnection}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Display Name (optional)"
                    value={mapping.displayName}
                    onChange={(e) => setMapping((prev) => ({ ...prev, displayName: e.target.value }))}
                    helperText="Optional label for this mapping"
                    disabled={!hasConnection}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mapping.isEnabled}
                        onChange={(e) => setMapping((prev) => ({ ...prev, isEnabled: e.target.checked }))}
                        disabled={!hasConnection}
                      />
                    }
                    label="Enabled"
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mapping.autoPushEnabled}
                        onChange={(e) => setMapping((prev) => ({ ...prev, autoPushEnabled: e.target.checked }))}
                        disabled={!hasConnection}
                      />
                    }
                    label="Auto Daily Push"
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleSave}
                  loading={saving}
                  disabled={!hasConnection}
                  startIcon={<Icon icon="material-symbols:save" />}
                >
                  Save Mapping
                </LoadingButton>
                {mapping.hasExisting && (
                  <LoadingButton
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    loading={saving}
                    startIcon={<Icon icon="mdi:delete-outline" />}
                  >
                    Remove Mapping
                  </LoadingButton>
                )}
                <Button
                  variant="outlined"
                  onClick={() => navigate('/sites')}
                >
                  Back to Sites
                </Button>
              </Box>
            </Box>
          </Card>
          {/* Daily Push Schedule Card */}
          {hasConnection && (
            <Card>
              <CardHeader
                title="Daily Push Schedule"
                subheader="Configure when the automatic daily push to Procore runs (applies to all sites on this account)"
              />
              <Box sx={{ p: 3, pt: 0 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid xs={12} md={6}>
                    <TextField
                      size="small"
                      fullWidth
                      select
                      label="Daily Push Hour (UTC)"
                      value={dailyPushHourUtc}
                      onChange={(e) => setDailyPushHourUtc(parseInt(e.target.value, 10))}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <MenuItem key={i} value={i}>
                          {String(i).padStart(2, '0')}:00 UTC
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid xs={12} md={6}>
                    {lastAutoPushDateUtc ? (
                      <Typography variant="body2" color="text.secondary">
                        Last auto-push: {new Date(lastAutoPushDateUtc).toLocaleDateString()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No auto-push has run yet
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <LoadingButton
                    variant="contained"
                    size="small"
                    onClick={handleSaveDailyPushHour}
                    loading={savingConnection}
                    startIcon={<Icon icon="material-symbols:save" />}
                  >
                    Save Schedule
                  </LoadingButton>
                </Box>
              </Box>
            </Card>
          )}
        </Stack>
      </Container>
    </>
  );
}
